import { useCallback, useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { CLINIC } from "@/constants/testIds";

// Fix P (2026-09-30): estado del numero de WhatsApp en Meta y pasos para
// conectarlo a la plataforma (Meta solo permite registrarlo por API).
const STATUS = { CONNECTED: "Conectado", PENDING: "Pendiente", OFFLINE: "Sin conectar", DISCONNECTED: "Desconectado", FLAGGED: "Con advertencia de Meta", RESTRICTED: "Restringido", BANNED: "Bloqueado", MIGRATED: "Migrado" };
const VERIFICATION = { VERIFIED: "Verificado", NOT_VERIFIED: "Sin verificar", EXPIRED: "Vencida" };
const QUALITY = { GREEN: "Alta", YELLOW: "Media", RED: "Baja", UNKNOWN: "Sin datos" };
const NAME = { APPROVED: "aprobado", AVAILABLE_WITHOUT_REVIEW: "aprobado", PENDING_REVIEW: "en revision", DECLINED: "rechazado", EXPIRED: "vencido", NONE: "sin aprobar" };

function Row({ label, value, testId }) {
  return (
    <div className="flex justify-between gap-4 text-sm py-1.5 border-b border-border last:border-0">
      <span className="text-muted-foreground">{label}</span>
      {/* Fix AI (2026-10-23): un texto largo (por ejemplo un error de Meta con un link) se corta en vez de salirse de la pantalla */}
      <span className="text-foreground text-right min-w-0 break-words" data-testid={testId}>{value ?? "-"}</span>
    </div>
  );
}

export default function WhatsAppMetaCard() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [code, setCode] = useState("");
  const [pin, setPin] = useState("");
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/whatsapp/meta/status");
      setInfo(data);
    } catch (err) {
      setInfo({ error: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async (key, path, body, okTitle) => {
    setBusy(key);
    try {
      await api.post(path, body);
      toast({ title: okTitle });
      await load();
      return true;
    } catch (err) {
      toast({ title: "Meta no lo acepto", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
      return false;
    } finally {
      setBusy("");
    }
  };

  const phone = info?.phone || {};
  const ready = info && !info.error;
  const subscribedText = !info ? null : info.subscribed === true ? `Si (${(info.subscribed_apps || []).filter(Boolean).join(", ") || "app conectada"})` : info.subscribed === false ? "No" : info.subscribed_error;

  return (
    <div className="bg-white border border-border rounded-xl p-6 sm:p-8 shadow-sm space-y-5" data-testid={CLINIC.whatsappMetaCard}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-serif text-xl text-foreground">WhatsApp oficial (Meta)</h2>
          <p className="text-sm text-muted-foreground mt-1">Estado del numero en Meta y conexion con la plataforma.</p>
        </div>
        <Button variant="outline" size="sm" className="gap-1.5 shrink-0" onClick={load} disabled={loading} data-testid={CLINIC.whatsappMetaRefresh}>
          <RefreshCw className="w-3.5 h-3.5" /> Revisar
        </Button>
      </div>

      {/* Fix AI (2026-10-23): un error largo de Meta (con links o codigos) se corta en vez de salirse de la pantalla */}
      {info?.error ? (
        <p className="text-sm text-destructive break-words">{info.error}</p>
      ) : (
        <div>
          <Row label="Canal que usa la plataforma" value={info ? (info.provider === "meta" ? "WhatsApp de Meta" : "Twilio (pruebas)") : null} testId={CLINIC.whatsappMetaProvider} />
          <Row label="Numero" value={phone.display_phone_number} />
          <Row label="Nombre que ven los clientes" value={phone.verified_name ? `${phone.verified_name} (${NAME[phone.name_status] || phone.name_status || "-"})` : null} />
          <Row label="Conexion" value={STATUS[phone.status] || phone.status} testId={CLINIC.whatsappMetaStatus} />
          <Row label="Verificacion del numero" value={VERIFICATION[phone.code_verification_status] || phone.code_verification_status} />
          <Row label="Calidad" value={QUALITY[phone.quality_rating] || phone.quality_rating} />
          <Row label="Los mensajes llegan a la plataforma" value={subscribedText} testId={CLINIC.whatsappMetaSubscribed} />
          <Row label="Plantillas aprobadas" value={info ? (info.templates_error || info.templates_approved) : null} />
          {info?.phone_error && <p className="text-sm text-destructive mt-2 break-words">{info.phone_error}</p>}
        </div>
      )}

      {ready && info.subscribed === false && (
        <Button variant="outline" disabled={Boolean(busy)} onClick={() => run("subscribe", "/whatsapp/meta/subscribe", {}, "Listo: los mensajes van a llegar a la plataforma")} data-testid={CLINIC.whatsappMetaSubscribeButton}>
          Hacer que los mensajes lleguen a la plataforma
        </Button>
      )}

      {ready && phone.status !== "CONNECTED" && (
        <div className="space-y-3 border-t border-border pt-4" data-testid={CLINIC.whatsappMetaConnect}>
          <p className="text-sm font-medium text-foreground">Conectar el numero</p>
          <p className="text-xs text-muted-foreground">
            Antes, borra la cuenta de WhatsApp Business del telefono. Si la verificacion no dice "Verificado", pedi el codigo (llega a este numero por SMS o por llamada) y verificalo.
            Despues elegi un PIN de 6 numeros y guardalo: Meta lo pide si hay que volver a conectar el numero. Meta permite 10 intentos de conexion cada 72 horas.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" disabled={Boolean(busy)} onClick={() => run("sms", "/whatsapp/meta/request-code", { method: "SMS" }, "Codigo enviado por SMS")} data-testid={CLINIC.whatsappMetaCodeSms}>
              Pedir codigo por SMS
            </Button>
            <Button variant="outline" size="sm" disabled={Boolean(busy)} onClick={() => run("voice", "/whatsapp/meta/request-code", { method: "VOICE" }, "Van a llamar al numero con el codigo")} data-testid={CLINIC.whatsappMetaCodeVoice}>
              Pedir codigo por llamada
            </Button>
          </div>
          <div className="flex gap-2">
            <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="Codigo de 6 numeros" inputMode="numeric" data-testid={CLINIC.whatsappMetaCodeInput} />
            <Button
              variant="outline"
              disabled={Boolean(busy) || code.length !== 6}
              onClick={async () => { if (await run("verify", "/whatsapp/meta/verify-code", { code }, "Numero verificado")) setCode(""); }}
              data-testid={CLINIC.whatsappMetaVerifyButton}
            >
              Verificar
            </Button>
          </div>
          <div className="flex gap-2">
            <Input type="password" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="PIN de 6 numeros" inputMode="numeric" autoComplete="new-password" data-testid={CLINIC.whatsappMetaPinInput} />
            <Button
              disabled={Boolean(busy) || pin.length !== 6}
              onClick={async () => { if (await run("register", "/whatsapp/meta/register", { pin }, "Numero conectado")) setPin(""); }}
              className="bg-brand hover:bg-brand-hover text-white"
              data-testid={CLINIC.whatsappMetaRegisterButton}
            >
              Conectar numero
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
